import React, { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { eventsApi } from "../utils/api";

const eventTypes = ["Pooja", "Cultural", "Competition", "Social", "Visarjana"];

const typeColors = {
  Pooja: "bg-orange-50 text-orange-700",
  Cultural: "bg-purple-50 text-purple-700",
  Competition: "bg-blue-50 text-blue-700",
  Social: "bg-green-50 text-green-700",
  Visarjana: "bg-red-50 text-red-700",
};

const typeIcons = { Pooja: "🕉️", Cultural: "🎭", Competition: "🏆", Social: "🍱", Visarjana: "🎉" };

function EventModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: "", date: "2026-09-14", time: "", venue: "", type: "Pooja", volunteers: 5 });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({ ...form, id: `EVT-${Date.now()}`, status: "upcoming", volunteers: Number(form.volunteers) });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl z-10">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Add Event</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Event Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Bhajan Sandhya" required
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Time</label>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Venue</label>
            <input type="text" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })}
              placeholder="Main Stage, Pandal..."
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                {eventTypes.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Volunteers Needed</label>
              <input type="number" value={form.volunteers} onChange={(e) => setForm({ ...form, volunteers: e.target.value })}
                min="1"
                className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>
          <button type="submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-xl transition-colors">
            Add Event
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("All");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    eventsApi.list()
      .then((res) => setEvents(res.items || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = filterType === "All" ? events : events.filter((e) => e.type === filterType);

  const handleSave = async (event) => {
    try {
      const saved = await eventsApi.create(event);
      setEvents([...events, saved.item || event]);
    } catch {
      setEvents([...events, event]);
    }
    setShowModal(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500">{events.length} events • Ganesh Chaturthi 2026</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-orange-500/20 text-sm">
          <Plus size={16} /> Add Event
        </button>
      </div>

      {/* Type Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {["All", ...eventTypes].map((t) => (
          <button key={t} onClick={() => setFilterType(t)}
            className={`px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
              filterType === t ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"
            }`}>
            {t !== "All" && <span className="mr-1">{typeIcons[t]}</span>}{t}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Event</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Type</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Date</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Time</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Venue</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Volunteers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-gray-800">{event.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${typeColors[event.type] || "bg-gray-50 text-gray-600"}`}>
                      {typeIcons[event.type]} {event.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {event.date ? new Date(event.date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{event.time || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{event.venue || "—"}</td>
                  <td className="px-4 py-3 text-center text-xs font-semibold text-gray-700">{event.volunteers || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile List */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map((event) => (
            <div key={event.id} className="px-4 py-3 flex items-center gap-3">
              <div className="text-xl flex-shrink-0">{typeIcons[event.type]}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{event.name}</p>
                <p className="text-xs text-gray-400">
                  {event.date ? new Date(event.date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : ""}
                  {event.time ? ` • ${event.time}` : ""}
                  {event.venue ? ` • ${event.venue}` : ""}
                </p>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${typeColors[event.type] || "bg-gray-50 text-gray-600"}`}>
                {event.type}
              </span>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">No events found</div>
        )}
      </div>

      {showModal && <EventModal onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
}
